import moment from 'moment';
import { useSelector } from 'react-redux';
import useSWR, { mutate } from 'swr';
import type { Cache, SWRConfiguration } from 'swr/_internal';
import useSWRMutation from 'swr/mutation';
import settings from '/@/core/settings';
import { _ } from '/@/locale';
import { axFetch } from '/@/store/session/actions';
import type { PaginatedResponse, PaginatedResponseWithFilters } from '/@/types/core';
import type { Recommendation, RecommendationComment } from '/@/types/recommendation';
import { invalidate } from '/@/utils/swr';
import { getAppInstance } from '../../session/selectors';
export const getSoilMoisturesFromAPI = async ({ lat, lng, countryCode, climatePattern, plantingDate = undefined }) => {
    try {
        const apiUrl = new URL(`${settings.urls.apiDomain}/core/v1/soil-moistures/`, window.location.origin);
        const params = new URLSearchParams();
        if (lat) params.append('lat', lat);
        if (lng) params.append('lng', lng);
        params.append('country', countryCode);
        params.append('climate_pattern', climatePattern);
        if (plantingDate) params.append('planting_date', plantingDate);
        apiUrl.search = params.toString();

        const res = await axFetch.get(apiUrl.toString());
        return res.data['results'].map((el) => ({
            value: el.id,
            label: el.name,
        }));
    } catch (error) {
        return [];
    }
};

export const getSoilTypesAndIrrigationChoicesFromAPI = async ({
    lat,
    lng,
    countryCode,
    climatePattern,
    soilMoisture,
    plantingDate = undefined,
}) => {
    try {
        const apiUrl = new URL(`${settings.urls.apiDomain}/core/v1/soil-types-and-irrigation/`, window.location.origin);
        const params = new URLSearchParams();
        params.append('lat', lat);
        params.append('lng', lng);
        params.append('country', countryCode);
        params.append('climate_pattern', climatePattern);
        if (plantingDate) params.append('planting_date', plantingDate);
        if (soilMoisture !== null && soilMoisture !== undefined) params.append('soil_moisture', soilMoisture);
        apiUrl.search = params.toString();

        const res = await axFetch.get(apiUrl.toString());
        return {
            soilTypesChoices: res.data.soilTypes
                .map((el) => {
                    if (el) {
                        return {
                            value: el.id,
                            label: el.name,
                        };
                    }
                    return el;
                })
                .sort((a, b) => a?.label.localeCompare(b?.label)),
            irrigationChoices: res.data.irrigationChoices?.map((el) => {
                const labels = {
                    null: _('Todos'),
                    0: _('No'),
                    1: _('Si'),
                };

                return {
                    value: el,
                    label: labels?.[el],
                };
            }),
        };
    } catch (error) {
        return {
            soilTypesChoices: [],
            irrigationChoices: [],
        };
    }
};

export const getEnvironmentsFromAPI = async ({
    lat,
    lng,
    countryCode,
    plantingDate,
    climatePattern,
    soilMoisture,
    crop,
    soilType,
    irrigation,
}): Promise<{
    percentiles: [number, number, number, number, number];
    min: number;
    max: number;
    default: number;
}> => {
    try {
        const apiUrl = new URL(`${settings.urls.apiDomain}/core/v1/estimated-environment/`, window.location.origin);
        const params = new URLSearchParams();
        params.append('lat', lat);
        params.append('lng', lng);
        params.append('country', countryCode);
        params.append('planting_date', plantingDate);
        if (crop === 'corn') {
            params.append('climate_pattern', climatePattern);
            // Locations without soil moisture data send none instead of an unusable value.
            if (![null, undefined].includes(soilMoisture)) {
                params.append('soil_moisture', soilMoisture);
            }
        } else {
            params.append('crop', crop);
        }
        if (![null, undefined].includes(soilType)) {
            params.append('soil_type', soilType);
        }
        if (![null, undefined].includes(irrigation)) {
            params.append('irrigation', irrigation);
        }
        apiUrl.search = params.toString();

        const res = await axFetch.get(apiUrl.toString());
        const output = res.data;
        output.configurable = true;
        return output;
    } catch (error) {
        return {
            min: 3000,
            max: 15000,
            default: 9000,
            percentiles: [],
        };
    }
};

const parseAPIProduct = (APIProduct, index) => {
    const output = {
        ...APIProduct,
        ranking: index + 1,
        label: `${APIProduct.catalogName}${(APIProduct.preliminar && ' *') || ''}`,
        created: moment(APIProduct.created),
        modified: moment(APIProduct.modified),
    };
    output.product = {
        brand: APIProduct.brand,
        catalogName: APIProduct.catalogName,
    };
    return output;
};

export const getProductsFromAPI = async ({
    lat,
    lng,
    plantingDate,
    brandCodename,
    crop,
    environment,
}: {
    lat: string;
    lng: string;
    plantingDate: string;
    brandCodename: string;
    crop: string;
    environment: string;
}) => {
    try {
        const apiUrl = new URL(`${settings.urls.apiDomain}/core/v1/products/`, window.location.origin);
        const params = new URLSearchParams();
        params.append('lat', lat);
        params.append('lng', lng);
        params.append('planting_date', plantingDate);
        if (brandCodename) params.append('brand', brandCodename);
        params.append('crop', crop);
        params.append('environment', environment);
        apiUrl.search = params.toString();

        const res = await axFetch.get(apiUrl.toString());

        return {
            featuredProducts: res.data.recommended.map((p, index) => parseAPIProduct(p, index)),
            otherProducts: res.data.other
                .map((p, index) => parseAPIProduct(p, index))
                .sort((a, b) => a.label.localeCompare(b.label)),
        };
    } catch (error) {
        return {
            featuredProducts: [],
            otherProducts: [],
        };
    }
};

export const getMetricsForProductFromAPI = async (
    {
        crop,
        productId,
    }: {
        crop: 'soy' | 'corn';
        productId: string;
    },
    cache?: Cache<any>
) => {
    try {
        const apiUrl = new URL(
            `${settings.urls.apiDomain}/core/v1/product-profile/${productId}/metrics/`,
            window.location.origin
        );
        const params = new URLSearchParams();
        params.append('crop', crop);

        apiUrl.search = params.toString();

        const cacheKey = `getMetricsForProductFromAPI-${crop}-${productId}`;
        if (cache) {
            const cachedRes = cache.get(cacheKey);
            if (cachedRes) return cachedRes;
        }
        const res = await axFetch.get(apiUrl.toString());
        if (cache && res.status == 200) {
            cache.set(cacheKey, res.data);
        }
        return res.data;
    } catch (error) {
        return [];
    }
};

export const getMetricsReferencesFromAPI = async () => {
    try {
        const apiUrl = new URL(`${settings.urls.apiDomain}/core/v1/metrics-references/`, window.location.origin);

        const res = await axFetch.get(apiUrl.toString());
        return res.data;
    } catch (error) {
        return [];
    }
};

export const getMetricsForProductsFromAPI = async (
    {
        crop,
        productsId,
    }: {
        crop: 'soy' | 'corn';
        productsId: string[];
    },
    cache?: Cache<any>
) => {
    try {
        const output = {};

        const allMetricsPromises = [];
        for (const productId of productsId) {
            allMetricsPromises.push(getMetricsForProductFromAPI({ crop, productId }, cache));
        }

        const allMetrics = await Promise.all(allMetricsPromises);

        productsId.forEach((productId, index) => {
            const metrics = allMetrics[index];
            output[productId] = metrics;
        });

        return output;
    } catch (error) {
        console.log(error);
        return {};
    }
};

export const getFeaturesForProductFromAPI = async ({ crop, productId }, cache?: Cache<any>) => {
    try {
        const apiUrl = new URL(
            `${settings.urls.apiDomain}/core/v1/product-profile/${productId}/features/`,
            window.location.origin
        );
        const params = new URLSearchParams();
        params.append('crop', crop);

        apiUrl.search = params.toString();

        const cacheKey = `getFeaturesForProductFromAPI-${crop}-${productId}`;
        if (cache) {
            const cachedRes = cache.get(cacheKey);
            if (cachedRes) return cachedRes;
        }
        const res = await axFetch.get(apiUrl.toString());
        if (cache && res.status == 200) {
            cache.set(cacheKey, res.data);
        }
        return res.data;
    } catch (error) {
        return [];
    }
};

export const getFeaturesForProductsFromAPI = async ({ crop, productsId }, cache?: Cache<any>) => {
    try {
        const output = {};

        const allFeaturesPromises = [];
        for (const productId of productsId) {
            allFeaturesPromises.push(getFeaturesForProductFromAPI({ crop, productId }, cache));
        }

        const allFeatures = await Promise.all(allFeaturesPromises);

        productsId.forEach((productId, index) => {
            const features = allFeatures[index];
            output[productId] = features;
        });

        return output;
    } catch (error) {
        return {};
    }
};

export const getCornDensityCurveFromAPI = async (
    { productId, environment, minValue, maxValue, step },
    cache?: Cache<any>
) => {
    try {
        const apiUrl = new URL(`${settings.urls.apiDomain}/core/v1/yield-density-curve/`, window.location.origin);
        const params = new URLSearchParams();
        params.append('product', productId);
        params.append('environment', environment);
        params.append('min_value', minValue);
        params.append('max_value', maxValue);
        params.append('step', step);
        apiUrl.search = params.toString();

        const cacheKey = `getCornDensityCurveFromAPI-${productId}-${environment}-${minValue}-${maxValue}-${step}`;
        if (cache) {
            const cachedRes = cache.get(cacheKey);
            if (cachedRes) return cachedRes;
        }
        const res = await axFetch.get(apiUrl.toString());
        if (cache) {
            cache.set(cacheKey, res.data);
        }
        return res.data;
    } catch (error) {
        return {
            points: [],
        };
    }
};

export const getCornDensityCurveForProductsFromAPI = async (
    { productsId, environment, minValue, maxValue, step },
    cache?: Cache<any>
) => {
    try {
        const output = {};
        const cornDensityCurvePromises = [];
        for (const productId of productsId) {
            cornDensityCurvePromises.push(
                getCornDensityCurveFromAPI({ productId, environment, minValue, maxValue, step }, cache)
            );
        }

        const cornDensityCurves = await Promise.all(cornDensityCurvePromises);

        productsId.forEach((productId, index) => {
            const cornDensityCurve = cornDensityCurves[index];
            output[productId] = cornDensityCurve;
        });

        return output;
    } catch (error) {
        return {};
    }
};

export const getRecommendationsFromAPI = async ({
    appInstance,
    page,
    filters,
    ordering,
    pageSize,
    year,
    node,
    includeChildren,
    excludeTargetNode,
    fieldName,
    fieldId,
    type,
}: {
    appInstance: string;
    page: number;
    filters: Record<string, any>;
    ordering?: string;
    pageSize?: number;
    year?: number;
    node?: number;
    includeChildren?: boolean;
    excludeTargetNode?: boolean;
    fieldName?: string;
    fieldId?: number;
    type?: string;
}): Promise<PaginatedResponseWithFilters<Recommendation>> => {
    try {
        const params = new URLSearchParams();
        params.append('app_instance', appInstance);
        params.append('page', String(page));
        if (pageSize) params.append('page_size', String(pageSize));
        if (year) params.append('year', String(year));
        if (node) params.append('node', String(node));
        if (includeChildren !== undefined) params.append('include_children', String(includeChildren));
        if (excludeTargetNode !== undefined) params.append('exclude_target_node', String(excludeTargetNode));
        if (ordering) params.append('ordering', ordering);
        if (fieldName) params.append('field_name', fieldName);
        if (fieldId) params.append('field_id', String(fieldId));
        if (type) params.append('type', type);
        for (const key in filters) {
            if (filters[key] !== undefined && filters[key] !== '') {
                if (key === 'created') {
                    const date = moment(filters[key], 'YYYY/MM/DD');
                    params.append(key, date.format('DD/MM/YYYY'));
                } else if (key === 'created_by__in' && Array.isArray(filters[key])) {
                    // Handle array filters (comma-separated)
                    params.append(key, filters[key].join(','));
                } else {
                    params.append(key, filters[key]);
                }
            }
        }

        const apiUrl = new URL(
            `${settings.urls.apiDomain}/core/v1/recommendation/?${params.toString()}`,
            window.location.origin
        );
        const res = await axFetch.get(apiUrl.toString());
        return res.data;
    } catch (error) {
        return {};
    }
};

export const useGetRecommendationsFromAPI = (
    {
        page,
        pageSize = 10,
        filters,
        ordering,
        year,
        fieldName,
        node,
        includeChildren,
        excludeTargetNode,
        fieldId,
        type,
    }: {
        page: number;
        pageSize?: number;
        filters: Record<string, any>;
        ordering?: string;
        year?: number;
        fieldName?: string;
        node?: number;
        includeChildren?: boolean;
        excludeTargetNode?: boolean;
        fieldId?: number;
        type?: string;
    },
    sWRConfiguration: SWRConfiguration | undefined = undefined,
    enabled = true
) => {
    const appInstance = useSelector(getAppInstance);
    const key = {
        key: 'myRecommendations',
        appInstance: appInstance.id,
        page,
        filters,
        ordering,
        pageSize,
        year,
        fieldName,
        node,
        includeChildren,
        excludeTargetNode,
        fieldId,
        type,
    };
    return useSWR(
        enabled ? key : null,
        ({
            appInstance,
            page,
            filters,
            ordering,
            pageSize,
            year,
            node,
            includeChildren,
            excludeTargetNode,
            fieldName,
            fieldId,
            type,
        }) =>
            getRecommendationsFromAPI({
                appInstance,
                page,
                filters,
                ordering,
                pageSize,
                year,
                node,
                includeChildren,
                excludeTargetNode,
                fieldName,
                fieldId,
                type,
            }),
        {
            revalidateOnMount: true,
            revalidateOnFocus: true,
            revalidateIfStale: true,
            revalidateOnReconnect: true,
            keepPreviousData: true,
            ...sWRConfiguration,
        }
    );
};

export const deleteRecommendationUsingAPI = async ({ id }) => {
    try {
        const apiUrl = new URL(`${settings.urls.apiDomain}/core/v1/recommendation/${id}/`, window.location.origin);

        const res = await axFetch.delete(apiUrl.toString());

        return res.data;
    } catch (error) {
        return [];
    }
};

export const useDeleteRecommendationMutation = () => {
    return useSWRMutation(
        { key: 'deleteRecommendation' },
        (key, { arg }: { arg: { id: number } }) => deleteRecommendationUsingAPI(arg),
        {
            onSuccess: () => {
                mutate((key) => typeof key === 'object' && key?.key == 'myRecommendations');
            },
        }
    );
};

export const getRecommendationByIdFromAPI = async ({ recoId }) => {
    if (!recoId) return;
    try {
        const apiUrl = new URL(`${settings.urls.apiDomain}/core/v1/recommendation/${recoId}/`, window.location.origin);

        const res = await axFetch.get(apiUrl.toString());
        return res.data;
    } catch (error) {
        return {};
    }
};

export const createRecommendationFromAPI = async ({
    appInstance,
    field,
    crops = [],
    weeds = [],
    status = 'draft',
    backup = {},
    weed_v2,
    disease,
    plague,
    seed_treatment,
    corn_density,
}) => {
    const apiUrl = new URL(`${settings.urls.apiDomain}/core/v1/recommendation/`, window.location.origin);

    const res = await axFetch.post(apiUrl.toString(), {
        status,
        appInstance: appInstance,
        field,
        crops: crops || [],
        weeds: weeds || [],
        weed_v2,
        disease,
        plague,
        seed_treatment,
        backup,
        corn_density,
    });

    mutate((key) => typeof key === 'object' && (key?.key == 'myRecommendations' || key?.key === 'myRecommendation'));

    return res.data;
};

export const updateRecommendationFromAPI = async ({
    id,
    status,
    crops,
    weeds,
    appInstance,
    field,
    backup,
    weed_v2,
    disease,
    plague,
    seed_treatment,
    corn_density,
}) => {
    try {
        const apiUrl = new URL(`${settings.urls.apiDomain}/core/v1/recommendation/${id}/`, window.location.origin);

        const recoData = {
            status: status || undefined,
            crops: crops || [],
            weeds: weeds || [],
            weed_v2,
            disease,
            plague,
            seed_treatment,
            appInstance,
            field,
            corn_density,
        };

        if (backup) recoData.backup = backup;
        const res = await axFetch.patch(apiUrl.toString(), recoData);

        mutate(
            (key) => typeof key === 'object' && (key?.key == 'myRecommendations' || key?.key === 'myRecommendation')
        );

        return res.data;
    } catch (error) {
        console.log(error);
        return {};
    }
};

export type PaginatedRecommendationCommentList = PaginatedResponse<RecommendationComment>;
export interface RecommendationCommentListParams {
    recommendation?: number;
    page?: number;
    page_size?: number;
    ordering?: string;
}

export const getRecommendationCommentsFromAPI = async (
    params: RecommendationCommentListParams & { key: string }
): Promise<PaginatedRecommendationCommentList> => {
    const { ...queryParams } = params;
    const searchParams = new URLSearchParams();

    if (queryParams.recommendation) searchParams.append('recommendation', String(queryParams.recommendation));
    if (queryParams.page) searchParams.append('page', String(queryParams.page));
    if (queryParams.page_size) searchParams.append('page_size', String(queryParams.page_size));
    if (queryParams.ordering) searchParams.append('ordering', queryParams.ordering);

    const apiUrl = new URL(
        `${settings.urls.apiDomain}/core/v1/recommendation/comments/?${searchParams.toString()}`,
        window.location.origin
    );

    const res = await axFetch.get(apiUrl.toString());
    return res.data;
};

export const getRecommendationCommentByIdFromAPI = async ({
    id,
}: {
    id: number;
    key: string;
}): Promise<RecommendationComment> => {
    const apiUrl = new URL(`${settings.urls.apiDomain}/core/v1/recommendation/comments/${id}/`, window.location.origin);

    const res = await axFetch.get(apiUrl.toString());
    return res.data;
};

export const createRecommendationCommentFromAPI = async ({
    recommendation,
    message,
}: {
    recommendation: number;
    message: string;
}): Promise<RecommendationComment> => {
    const apiUrl = new URL(`${settings.urls.apiDomain}/core/v1/recommendation/comments/`, window.location.origin);

    const res = await axFetch.post(apiUrl.toString(), {
        recommendation,
        message,
    });

    invalidate({ key: 'recommendationComments' });
    invalidate('myRecommendations');
    invalidate('myRecommendation');

    return res.data;
};

export const updateRecommendationCommentFromAPI = async ({
    id,
    message,
}: {
    id: number;
    message: string;
}): Promise<RecommendationComment> => {
    const apiUrl = new URL(`${settings.urls.apiDomain}/core/v1/recommendation/comments/${id}/`, window.location.origin);

    const res = await axFetch.patch(apiUrl.toString(), {
        message,
    });

    // Update comments and refresh recommendation lists/details (for commentsStatus)
    mutate((key) => typeof key === 'object' && key?.key === 'recommendationComments');
    invalidate('myRecommendations');
    invalidate('myRecommendation');

    return res.data;
};

export const deleteRecommendationCommentFromAPI = async ({ id }: { id: number }): Promise<void> => {
    const apiUrl = new URL(`${settings.urls.apiDomain}/core/v1/recommendation/comments/${id}/`, window.location.origin);

    await axFetch.delete(apiUrl.toString());

    invalidate({ key: 'recommendationComments' });
    invalidate('myRecommendations');
    invalidate('myRecommendation');
};

export const markRecommendationCommentsAsReadFromAPI = async ({
    recommendation,
}: {
    recommendation: number;
}): Promise<{ message: string; count: number }> => {
    const apiUrl = new URL(
        `${settings.urls.apiDomain}/core/v1/recommendation/comments/mark-as-read/`,
        window.location.origin
    );

    const res = await axFetch.post(apiUrl.toString(), {
        recommendation,
    });

    // Invalidate comments cache to refresh read status
    invalidate({ key: 'recommendationComments' });
    invalidate('myRecommendations');
    invalidate('myRecommendation');

    return res.data;
};

export const shareRecommendationFromAPI = async ({
    recommendationId,
    sharedWith,
}: {
    recommendationId: number;
    sharedWith: number;
}): Promise<any> => {
    const apiUrl = new URL(
        `${settings.urls.apiDomain}/core/v1/recommendation/${recommendationId}/share/`,
        window.location.origin
    );

    const res = await axFetch.put(apiUrl.toString(), {
        shared_with: sharedWith,
    });

    // Invalidate recommendation caches to refresh shared status
    invalidate('myRecommendations');
    invalidate('myRecommendation');

    return res.data;
};

export const unshareRecommendationFromAPI = async ({
    recommendationId,
    sharedWithId,
}: {
    recommendationId: number;
    sharedWithId: number;
}): Promise<void> => {
    const apiUrl = new URL(
        `${settings.urls.apiDomain}/core/v1/recommendation/${recommendationId}/share/${sharedWithId}/`,
        window.location.origin
    );

    await axFetch.delete(apiUrl.toString());

    // Invalidate recommendation caches to refresh shared status
    invalidate('myRecommendations');
    invalidate('myRecommendation');
};

export const getSoyPlantingRecommendationFromAPI = async (
    { productId, plantingDate, environment },
    cache?: Cache<any>
) => {
    try {
        const apiUrl = new URL(
            `${settings.urls.apiDomain}/core/v1/soy/planting-recommendation/`,
            window.location.origin
        );
        const params = new URLSearchParams();
        params.append('product', productId);
        params.append('planting_date', plantingDate);
        params.append('environment', environment);

        apiUrl.search = params.toString();

        const cacheKey = `getSoyPlantingRecommendationFromAPI-${productId}-${plantingDate}-${environment}`;
        if (cache) {
            const cachedRes = cache.get(cacheKey);
            if (cachedRes) return cachedRes;
        }
        const res = await axFetch.get(apiUrl.toString());
        if (cache && res.status == 200) {
            cache.set(cacheKey, res.data);
        }

        return res.data;
    } catch (error) {
        return [];
    }
};

export const getAllSoyPlantingRecommendationFromAPI = async (
    { productsId, plantingDate, environment },
    cache?: Cache<any>
) => {
    try {
        const output = {};

        const allPlantingWindowPromises = [];
        for (const productId of productsId) {
            allPlantingWindowPromises.push(
                getSoyPlantingRecommendationFromAPI({ productId, plantingDate, environment }, cache)
            );
        }

        const allPlantingWindowData = await Promise.all(allPlantingWindowPromises);

        productsId.forEach((productId, index) => {
            const data = allPlantingWindowData[index];
            output[productId] = data;
        });

        return output;
    } catch (error) {
        return {};
    }
};

export const getFertilizationCoefficientsForProductFromAPI = async (
    { environment, estimatedYield, productId },
    cache?: Cache<any>
) => {
    try {
        const apiUrl = new URL(`${settings.urls.apiDomain}/core/v1/fertilization/`, window.location.origin);
        const params = new URLSearchParams();
        params.append('environment', environment);
        params.append('estimated_yield', estimatedYield);
        params.append('product', productId);

        apiUrl.search = params.toString();

        const cacheKey = `getFertilizationCoefficientsForProductFromAPI-${environment}-${estimatedYield}-${productId}`;
        if (cache) {
            const cachedRes = cache.get(cacheKey);
            if (cachedRes) return cachedRes;
        }
        const res = await axFetch.get(apiUrl.toString());
        if (cache && res.status == 200) {
            cache.set(cacheKey, res.data);
        }
        return res.data;
    } catch (error) {
        return {};
    }
};

export type getFertilizationCoefficientsForProductsFromAPIProps = {
    environment: number;
    products: {
        id: number;
        estimatedYield: number;
    }[];
};
export const getFertilizationCoefficientsForProductsFromAPI = async (
    { environment, products }: getFertilizationCoefficientsForProductsFromAPIProps,
    cache?: Cache<any>
): Record<
    number,
    {
        n:
            | {
                  common: number;
                  recommended: number;
              }
            | number;
        p: number;
        k: number;
        ca: number;
        mg: number;
        s: number;
        b: number;
        cl: number;
        cu: number;
        fe: number;
        mn: number;
        mo: number;
        zn: number;
        grainMoisture: number;
    }
> => {
    try {
        const output = {};

        const allFertilizationCoefficientsPromises = [];
        for (const product of products) {
            allFertilizationCoefficientsPromises.push(
                getFertilizationCoefficientsForProductFromAPI(
                    {
                        environment,
                        estimatedYield: product.estimatedYield,
                        productId: product.id,
                    },
                    cache
                )
            );
        }

        const allFertilizationCoefficients = await Promise.all(allFertilizationCoefficientsPromises);

        products.forEach((product, index) => {
            const fertilizationCoefficients = allFertilizationCoefficients[index];
            output[product.id] = fertilizationCoefficients;
        });

        return output;
    } catch (error) {
        return {};
    }
};

export const getYieldByEnvironmentForProductFromAPI = async (
    { productId, harvestDensity, environment },
    cache?: Cache<any>
) => {
    try {
        const apiUrl = new URL(`${settings.urls.apiDomain}/core/v1/yield-by-environment/`, window.location.origin);
        const params = new URLSearchParams();
        params.append('product', productId);
        params.append('environment', environment);
        params.append('harvest_density', harvestDensity);

        apiUrl.search = params.toString();

        const cacheKey = `getYieldByEnvironmentFromAPI-${productId}-${harvestDensity}-${environment}`;
        if (cache) {
            const cachedRes = cache.get(cacheKey);
            if (cachedRes) return cachedRes;
        }
        const res = await axFetch.get(apiUrl.toString());
        if (cache && res.status == 200) {
            cache.set(cacheKey, res.data);
        }

        return res.data;
    } catch (error) {
        return [];
    }
};

export const getYieldByEnvironmentForProductsFromAPI = async (
    { productsId, harvestDensity, environment },
    cache?: Cache<any>
) => {
    try {
        const output = {};

        const allYieldByEnvironmentPromises = [];
        for (const productId of productsId) {
            allYieldByEnvironmentPromises.push(
                getYieldByEnvironmentForProductFromAPI({ productId, harvestDensity, environment }, cache)
            );
        }

        const allYieldByEnvironmentData = await Promise.all(allYieldByEnvironmentPromises);

        productsId.forEach((productId, index) => {
            const data = allYieldByEnvironmentData[index];
            output[productId] = data;
        });

        return output;
    } catch (error) {
        return {};
    }
};

export const getPhenologyForProductFromAPI = async ({ lat, lng, productId, plantingDate }, cache?: Cache<any>) => {
    try {
        const apiUrl = new URL(`${settings.urls.apiDomain}/core/v1/phenology/`, window.location.origin);
        const params = new URLSearchParams();
        params.append('product', productId);
        params.append('lat', lat);
        params.append('lng', lng);
        params.append('planting_date', plantingDate);

        apiUrl.search = params.toString();

        const cacheKey = `getPhenologyForProductFromAPI-${productId}-${lat}-${lng}-${plantingDate}`;
        if (cache) {
            const cachedRes = cache.get(cacheKey);
            if (cachedRes) return cachedRes;
        }
        const res = await axFetch.get(apiUrl.toString());
        if (cache && res.status == 200) {
            cache.set(cacheKey, res.data);
        }
        return res.data;
    } catch (error) {
        return [];
    }
};

export const getPhenologyForProductsFromAPI = async ({ lat, lng, productsId, plantingDate }, cache?: Cache<any>) => {
    try {
        const output = {};

        const allPhenologyPromises = [];
        for (const productId of productsId) {
            allPhenologyPromises.push(getPhenologyForProductFromAPI({ lat, lng, productId, plantingDate }, cache));
        }

        const allPhenology = await Promise.all(allPhenologyPromises);

        productsId.forEach((productId, index) => {
            const features = allPhenology[index];
            output[productId] = features;
        });

        return output;
    } catch (error) {
        return {};
    }
};

export const getPestControlPlansFromAPI = async ({
    crop,
    country,
    page,
    pageSize = 1000,
}: {
    crop: string;
    country: string;
    page?: number;
    pageSize?: number;
}) => {
    const apiUrl = new URL(
        `${settings.urls.apiDomain}/core/v1/pathologies/pest-control-plans/`,
        window.location.origin
    );
    const params = new URLSearchParams();
    params.append('crop', crop);
    params.append('country', country);
    params.append('page', page?.toString() ?? '');
    params.append('page_size', pageSize?.toString() ?? '');
    apiUrl.search = params.toString();

    const res = await axFetch.get(apiUrl.toString());
    return res.data;
};

export const getPestsFromAPI = async ({ page, pageSize = 1000 }: { page?: number; pageSize?: number }) => {
    const apiUrl = new URL(`${settings.urls.apiDomain}/core/v1/pathologies/pests/`, window.location.origin);
    const params = new URLSearchParams();
    params.append('page', page?.toString() ?? '');
    params.append('page_size', pageSize?.toString() ?? '');
    apiUrl.search = params.toString();

    const res = await axFetch.get(apiUrl.toString());
    return res.data;
};
